import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Loader2, Zap } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Message } from "@/components/types";
import { supabase } from "@/lib/supabase";
import { useTranslation } from 'react-i18next';

interface MysteryChatProps {
  initialTheme?: string;
  initialPlayerCount?: number;
  initialHasAccomplice?: boolean;
  initialScriptType?: 'full' | 'pointForm';
  initialAdditionalDetails?: string;
  initialMysteryType?: 'murder' | 'intrigue';
  initialMysteryStyle?: string | null; // 'character' (slip) | 'detective'; sent to mystery-ai so the chat knows whether the culprit is drawn (ADR-0181)
  savedMysteryId?: string;
  onSave: (message: Message) => Promise<void>;
  onGenerateFinal?: (messages: Message[]) => Promise<void>;
  initialMessages?: Message[];
  isLoadingHistory?: boolean;
  systemInstruction?: string;
  preventDuplicateMessages?: boolean;
  skipForm?: boolean;
  needsInitialAIResponse?: boolean;
  usePageScroll?: boolean;
  // A paid order was already created from this chat: show the history only, no input and no generate button.
  readOnly?: boolean;
}

export default function MysteryChat({
  initialTheme,
  initialPlayerCount,
  initialHasAccomplice,
  initialScriptType,
  initialAdditionalDetails,
  initialMysteryType,
  initialMysteryStyle,
  savedMysteryId,
  onSave,
  onGenerateFinal,
  initialMessages = [],
  isLoadingHistory = false,
  systemInstruction,
  preventDuplicateMessages = false,
  skipForm = false,
  needsInitialAIResponse = false,
  usePageScroll = false,
  readOnly = false
}: MysteryChatProps) {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(initialTheme || '');
  const [currentPlayerCount, setCurrentPlayerCount] = useState<number | null>(initialPlayerCount || null);
  const [currentHasAccomplice, setCurrentHasAccomplice] = useState(initialHasAccomplice || false);
  const [currentScriptType, setCurrentScriptType] = useState(initialScriptType || 'full');
  const [currentAdditionalDetails, setCurrentAdditionalDetails] = useState(initialAdditionalDetails || '');
  const [currentMysteryType, setCurrentMysteryType] = useState<'murder' | 'intrigue'>(initialMysteryType || 'murder');
  const [hasTriggeredInitialResponse, setHasTriggeredInitialResponse] = useState(false);
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Helper function to ensure proper timestamp handling
  const ensureValidTimestamp = (message: any): Message => {
    let timestamp: Date;
    
    if (message.timestamp) {
      timestamp = message.timestamp instanceof Date ? message.timestamp : new Date(message.timestamp);
    } else if (message.created_at) {
      timestamp = new Date(message.created_at);
    } else {
      timestamp = new Date();
    }

    return {
      id: message.id || Date.now().toString(),
      content: message.content || '',
      is_ai: message.is_ai !== undefined ? message.is_ai : (message.role === 'assistant'),
      timestamp: timestamp,
      isAutoGenerated: message.isAutoGenerated || false
    };
  };

  useEffect(() => {
    const processedMessages = initialMessages.map(ensureValidTimestamp);
    setMessages(processedMessages);
  }, [initialMessages]);

  // Enhanced effect to handle initial AI response
  useEffect(() => {
    if (needsInitialAIResponse && !hasTriggeredInitialResponse && messages.length > 0 && !isLoadingHistory) {
      // Don't trigger if the last message is already from the AI
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.is_ai) {
        console.log("Skipping initial AI response — last message is already from AI");
        return;
      }

      console.log("Triggering initial AI response for new conversation");
      setHasTriggeredInitialResponse(true);

      // Start AI typing immediately
      setIsAiTyping(true);

      // Find the last user message to respond to
      const lastUserMessage = messages.filter(msg => !msg.is_ai).pop();
      if (lastUserMessage) {
        triggerAIResponse(lastUserMessage.content);
      }
    }
  }, [needsInitialAIResponse, hasTriggeredInitialResponse, messages, isLoadingHistory]);

  // Scroll to bottom when messages change or AI is typing
  useEffect(() => {
    if (!usePageScroll) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    } else {
      // For page-level scrolling, scroll to bottom of page
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, isAiTyping, usePageScroll]);

  useEffect(() => {
    if (initialTheme) {
      setCurrentTheme(initialTheme);
    }
    if (initialPlayerCount) {
      setCurrentPlayerCount(initialPlayerCount);
    }
    if (initialHasAccomplice) {
      setCurrentHasAccomplice(initialHasAccomplice);
    }
    if (initialScriptType) {
      setCurrentScriptType(initialScriptType);
    }
    if (initialAdditionalDetails) {
      setCurrentAdditionalDetails(initialAdditionalDetails);
    }
    if (initialMysteryType) {
      setCurrentMysteryType(initialMysteryType);
    }
  }, [initialTheme, initialPlayerCount, initialHasAccomplice, initialScriptType, initialAdditionalDetails, initialMysteryType]);

  // FIXED: Enhanced function to properly detect if we have player count info
  const hasPlayerCountInfo = () => {
    // Don't consider initialPlayerCount as "having info" unless it's explicitly set and not a default
    if (initialPlayerCount && initialPlayerCount >= 4 && initialPlayerCount <= 50 && currentPlayerCount && currentPlayerCount >= 4 && currentPlayerCount <= 50) {
      console.log("Has explicit valid player count from props:", initialPlayerCount);
      return true;
    }
    
    // Check if any message explicitly mentions valid player count
    const hasPlayerCountInMessages = messages.some(msg => {
      const content = msg.content.toLowerCase();
      // Look for explicit player count mentions with numbers in valid range
      const playerCountMatch = content.match(/(\d+)\s*(player|people|guest|character|participant)/i);
      if (playerCountMatch) {
        const count = parseInt(playerCountMatch[1]);
        if (count >= 4 && count <= 50) {
          console.log("Found valid player count in message:", content.substring(0, 100));
          return true;
        }
      }
      return false;
    });
    
    console.log("Has player count in messages:", hasPlayerCountInMessages);
    return hasPlayerCountInMessages;
  };

  // Helper function to detect if we have theme information
  const hasThemeInfo = () => {
    const hasTheme = currentTheme && currentTheme.trim() !== '' && currentTheme !== 'Murder Mystery';
    console.log("Has theme info:", hasTheme, "Theme:", currentTheme);
    return hasTheme;
  };

  // FIXED: More accurate detection of new conversations that need player count question
  const isNewConversation = () => {
    const playerCountInfo = hasPlayerCountInfo();
    const isNew = messages.length <= 1 && !playerCountInfo;
    console.log("Is new conversation check:", {
      messageCount: messages.length,
      hasPlayerCount: playerCountInfo,
      isNew: isNew
    });
    return isNew;
  };

  const createSystemMessage = (data: {
    theme?: string;
    playerCount?: number | null;
    hasAccomplice?: boolean;
    scriptType?: string;
    additionalDetails?: string;
    language?: string;
  }) => {
    let behavioralInstructions = "";
    
    // Add language instruction based on current language
    if (data.language && data.language !== 'en') {
      const languageNames: {[key: string]: string} = {
        'ko': 'Korean',
        'de': 'German', 
        'es': 'Spanish',
        'fr': 'French',
        'da': 'Danish',
        'fi': 'Finnish',
        'it': 'Italian',
        'ja': 'Japanese',
        'nl': 'Dutch',
        'pt': 'Portuguese',
        'sv': 'Swedish',
        'zh-cn': 'Chinese'
      };
      const languageName = languageNames[data.language] || data.language;
      behavioralInstructions += `CRITICAL: You MUST respond entirely in ${languageName} language. `;
    }
    
    behavioralInstructions += `BEHAVIORAL RULES:
1. DO NOT ask for player count, script type, or accomplice preferences - this information is already provided
2. DO NOT ask clarifying questions about details already specified  
3. CREATE a complete murder mystery concept immediately
4. Follow their specifications exactly

`;
    
    return behavioralInstructions;
  };

  // Enhanced AI response function with retry logic
  const callAIWithRetry = async (messages: any[], systemPrompt: string | null, retryCount = 0, language?: string): Promise<any> => {
    const maxRetries = 3;
    
    try {
      console.log(`Attempt ${retryCount + 1} - Calling mystery-ai function`);
      
      const requestBody: any = {
        messages: messages,
        promptVersion: 'free',
        preventTruncation: true,
        language: language || 'en', // Default to English if no language is provided
        mysteryType: currentMysteryType,
        ...(initialMysteryStyle ? { mysteryStyle: initialMysteryStyle } : {})
      };

      // Only add system prompt if we have one
      if (systemPrompt) {
        requestBody.system = systemPrompt;
      }

      const { data, error } = await supabase.functions.invoke('mystery-ai', {
        body: requestBody
      });

      if (error) {
        // The Edge Function's actual error body (e.g. { error, code }) lives on
        // error.context (the raw Response) — error.message from supabase-js is
        // just a generic "non-2xx status code" string. Read the body so callers
        // can tell a deterministic validation failure (message too long) apart
        // from a transient one worth retrying.
        let errorMessage = error.message;
        let errorCode: string | undefined;
        if (error.context && typeof error.context.json === 'function') {
          try {
            const body = await error.context.json();
            if (body?.error) errorMessage = body.error;
            if (body?.code) errorCode = body.code;
          } catch {
            // Body wasn't JSON or already consumed — fall back to the generic message.
          }
        }
        const wrapped = new Error(errorMessage);
        if (errorCode) (wrapped as any).code = errorCode;
        throw wrapped;
      }

      if (data && data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) {
        return data.choices[0].message.content;
      } else if (data && data.error) {
        const wrapped = new Error(data.error);
        if (data.code) (wrapped as any).code = data.code;
        throw wrapped;
      } else {
        throw new Error('Invalid response format from AI service');
      }
    } catch (error) {
      console.error(`Attempt ${retryCount + 1} failed:`, error);

      // MESSAGE_TOO_LONG is a deterministic validation failure — the same
      // oversized message will be rejected identically on every retry, so
      // retrying just delays telling the user for no benefit.
      if ((error as any)?.code === 'MESSAGE_TOO_LONG') {
        throw error;
      }

      if (retryCount < maxRetries) {
        console.log(`Retrying in ${(retryCount + 1) * 1000}ms...`);
        await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 1000));
        return callAIWithRetry(messages, systemPrompt, retryCount + 1, language);
      } else {
        throw error;
      }
    }
  };

  // Turns a failed AI call into a persisted chat bubble instead of a toast
  // that vanishes with no trace in the transcript — a customer whose message
  // got rejected (e.g. too long) would otherwise see the conversation just
  // go silent with nothing explaining why (ADR-0103, 2026-09-13).
  const appendErrorMessage = async (error: unknown) => {
    const content = (error as any)?.code === 'MESSAGE_TOO_LONG'
      ? (error as Error).message
      : t("chat.errors.aiUnavailable");

    const errorMessage: Message = {
      id: (Date.now() + 1).toString(),
      content,
      is_ai: true,
      timestamp: new Date(),
      isAutoGenerated: false
    };

    setMessages(prev => [...prev, errorMessage]);

    try {
      await onSave(errorMessage);
    } catch (saveError) {
      console.error("Error saving error message:", saveError);
    }
  };

  const triggerAIResponse = async (userContent: string) => {
    // Safety guard: don't send a request if the last message is already from the AI
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.is_ai) {
      console.log("Aborting triggerAIResponse — last message is already from AI");
      setIsAiTyping(false);
      return;
    }

    try {
      console.log("=== Starting AI Request for Initial Response ===");

      // Use null to let Edge Function use MYSTERY_FREE_PROMPT environment variable
      const systemPrompt = null;

      const messagesToSend = messages.map(msg => ({
        role: msg.is_ai ? "assistant" : "user",
        content: msg.content
      }));

      const aiResponse = await callAIWithRetry(messagesToSend, systemPrompt, 0, currentLanguage);
      console.log("AI response content:", aiResponse);

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: aiResponse,
        is_ai: true,
        timestamp: new Date(),
        isAutoGenerated: false
      };

      const updatedMessages = [...messages, aiMessage];
      setMessages(updatedMessages);

      try {
        await onSave(aiMessage);
      } catch {
        try {
          await onSave(aiMessage);
        } catch (error) {
          console.error("Error saving AI message:", error);
          toast.error(t("chat.errors.saveFailed"));
        }
      }
    } catch (error) {
      console.error('Error calling AI service:', error);
      await appendErrorMessage(error);
      toast.error(t("chat.errors.responseFailed"));
    } finally {
      setIsAiTyping(false);
    }
  };

  const handleSendMessage = async (content: string) => {
    if (!content.trim()) return;

    setInput('');

    const userMessage: Message = {
      id: Date.now().toString(),
      content: content.trim(),
      is_ai: false,
      timestamp: new Date(),
      isAutoGenerated: false
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);

    try {
      await onSave(userMessage);
    } catch {
      // One retry for transient network blips, then warn — the message is
      // visible in the UI but NOT persisted; without a warning it silently
      // vanishes on refresh.
      try {
        await onSave(userMessage);
      } catch (error) {
        console.error("Error saving user message:", error);
        toast.error(t("chat.errors.saveFailed"));
      }
    }

    setIsAiTyping(true);

    try {
      // Use null to let Edge Function use MYSTERY_FREE_PROMPT environment variable
      const systemPrompt = null;

      const messagesToSend = newMessages.map(msg => ({
        role: msg.is_ai ? "assistant" : "user",
        content: msg.content
      }));

      const aiResponse = await callAIWithRetry(messagesToSend, systemPrompt, 0, currentLanguage);

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: aiResponse,
        is_ai: true,
        timestamp: new Date(),
        isAutoGenerated: false
      };

      const updatedMessages = [...newMessages, aiMessage];
      setMessages(updatedMessages);

      try {
        await onSave(aiMessage);
      } catch {
        try {
          await onSave(aiMessage);
        } catch (error) {
          console.error("Error saving AI message:", error);
          toast.error(t("chat.errors.saveFailed"));
        }
      }
    } catch (error) {
      console.error('Error calling AI service:', error);
      await appendErrorMessage(error);
      toast.error(t("chat.errors.responseFailed"));
    } finally {
      setIsAiTyping(false);
    }
  };

  // Auto-resize textarea
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    
    // Auto-resize
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 150)}px`;
    }
  };

  // Check if AI has provided at least one response
  const hasAIResponse = messages.some(msg => msg.is_ai);

  return (
    <div className={cn(
      "flex flex-col bg-muted",
      usePageScroll ? "h-full" : "h-full space-y-4 sm:space-y-6"
    )}>
      {/* Chat Messages Area */}
      <div className={cn(
        "space-y-4 sm:space-y-6 bg-muted",
        usePageScroll ? "pb-48" : "overflow-y-auto h-80 p-3 sm:h-96 sm:p-4"
      )}>
        {/* Loading History */}
        {isLoadingHistory && (
          <div className="text-center text-muted-foreground py-4">
            <Loader2 className={cn(
              "animate-spin text-primary",
              isMobile ? "h-5 w-5" : "h-6 w-6"
            )} />
            <p className={cn(
              "text-sm",
              isMobile ? "text-sm" : "text-base"
            )}>{t("chat.loadingMessages")}</p>
          </div>
        )}
        
        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              "flex",
              message.is_ai ? "justify-start" : "justify-end"
            )}
          >
            <div
              className={cn(
                "rounded-2xl px-4 py-3 sm:px-5 sm:py-4 max-w-[85%] sm:max-w-[80%] shadow-lg",
                message.is_ai
                  ? "text-foreground"
                  : "bg-primary text-white border-none"
              )}
              style={message.is_ai ? { backgroundColor: 'var(--color-charcoal)', border: '1px solid rgba(245,240,232,0.1)' } : undefined}
            >
              {message.is_ai ? (
                <div className={cn(
                  "prose prose-sm max-w-none",
                  isMobile && "text-sm leading-relaxed"
                )}>
                  <ReactMarkdown 
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      h1: ({node, ...props}) => <h1 className={cn(
                        "font-bold mt-3 mb-2 uppercase tracking-wide",
                        isMobile ? "text-lg" : "text-2xl"
                      )} style={{ fontFamily: 'var(--font-display)', color: '#E53E2A' }} {...props} />,
                      h2: ({node, ...props}) => <h2 className={cn(
                        "font-semibold mt-3 mb-1 uppercase tracking-wide",
                        isMobile ? "text-base" : "text-xl"
                      )} style={{ fontFamily: 'var(--font-display)', color: '#E53E2A' }} {...props} />,
                      h3: ({node, ...props}) => <h3 className={cn(
                        "font-medium mt-2 mb-1 text-foreground",
                        isMobile ? "text-sm" : "text-lg"
                      )} {...props} />,
                      p: ({node, ...props}) => <p className="my-1 leading-relaxed text-foreground" {...props} />,
                      ul: ({node, ...props}) => <ul className={cn(
                        "list-disc my-2",
                        isMobile ? "pl-4" : "pl-6"
                      )} {...props} />,
                      ol: ({node, ...props}) => <ol className={cn(
                        "list-decimal my-2",
                        isMobile ? "pl-4" : "pl-6"
                      )} {...props} />,
                      li: ({node, ...props}) => <li className="my-1 text-foreground" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-bold text-foreground" {...props} />,
                      em: ({node, ...props}) => <em className="italic" {...props} />,
                      blockquote: ({node, ...props}) => <blockquote className="pl-3 border-l-3 border-primary bg-muted italic my-2 rounded-r-md p-2" {...props} />
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className={cn(
                  "whitespace-pre-line leading-relaxed",
                  isMobile ? "text-sm" : "text-sm"
                )}>{message.content}</p>
              )}
            </div>
          </div>
        ))}
        
        {/* AI Typing Indicator - Mobile Optimized */}
        {isAiTyping && (
          <div className="flex justify-start">
            <div className="rounded-2xl px-4 py-3 sm:px-5 sm:py-4 max-w-[85%] sm:max-w-[80%] shadow-lg" style={{ backgroundColor: 'var(--color-charcoal)', border: '1px solid rgba(245,240,232,0.1)' }}>
              <div className="flex space-x-1">
                <div className="h-3 w-3 rounded-full animate-bounce" style={{ backgroundColor: '#C81400', animationDelay: "0ms" }}></div>
                <div className="h-3 w-3 rounded-full animate-bounce" style={{ backgroundColor: '#C81400', animationDelay: "150ms" }}></div>
                <div className="h-3 w-3 rounded-full animate-bounce" style={{ backgroundColor: '#C81400', animationDelay: "300ms" }}></div>
              </div>
              {/* Show helpful message during initial concept generation (no AI messages yet) */}
              {!messages.some(m => m.is_ai) && (
                <p className="text-xs sm:text-sm mt-2 opacity-70" style={{ color: 'var(--color-cream)' }}>
                  {t('chat.generatingConcept', 'Crafting your mystery concept — this can take up to 30 seconds...')}
                </p>
              )}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Fixed Bottom Generate Button and Text Input */}
      {!readOnly && (
      <div
        className={cn(
          "fixed left-0 right-0 z-20",
          isMobile ? "bottom-16 px-3 py-2" : "bottom-0 px-4 py-3"
        )}
        style={{ backgroundColor: 'var(--color-charcoal)', borderTop: '1px solid rgba(245,240,232,0.1)' }}
      >
        <div className={cn(
          "mx-auto space-y-3",
          isMobile ? "max-w-full" : "max-w-4xl"
        )}>
          {/* Text Input */}
          <div
            className="flex items-center gap-2 rounded-xl p-1.5"
            style={{ backgroundColor: 'var(--color-charcoal)', border: '1px solid rgba(245,240,232,0.2)' }}
          >
            <div className="flex-grow">
              <Textarea
                ref={inputRef}
                placeholder={t("chat.placeholder")}
                value={input}
                onChange={handleTextareaChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(input);
                  }
                }}
                disabled={isAiTyping}
                className={cn(
                  "resize-none border-0 shadow-none focus-visible:ring-0 min-h-[44px] bg-transparent",
                  isMobile ? "text-base" : "text-sm"
                )}
                style={{ color: 'var(--color-cream)', backgroundColor: 'transparent', border: 'none' }}
                rows={1}
              />
            </div>
            <button
              type="submit"
              onClick={() => handleSendMessage(input)}
              disabled={isAiTyping || !input.trim()}
              className="shrink-0 flex items-center justify-center rounded-lg transition-colors disabled:opacity-40"
              style={{
                width: isMobile ? '44px' : '36px',
                height: isMobile ? '44px' : '36px',
                backgroundColor: 'var(--color-red)',
                color: 'var(--color-cream)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {isAiTyping ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
      
          {/* Generate Full Mystery Button - NOW ON BOTTOM */}
          {onGenerateFinal && (
            <Button
              onClick={() => onGenerateFinal(messages)}
              disabled={!hasAIResponse}
              size={isMobile ? "default" : "lg"}
              className={cn(
                "w-full bg-primary hover:bg-primary/90 text-white font-medium shadow-sm rounded-xl",
                isMobile ? "h-12 text-base" : "px-6"
              )}
            >
              <Zap className="h-4 w-4 mr-2" />
              {t("mysteryCreation.buttons.generateFullMystery")}
            </Button>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
